import type { ChatSession, GenerationConfig, Message } from '../types';

/**
 * Puts the clarify state back on the turns of a conversation fetched from the
 * server.
 *
 * The state rides from the turn that asks cards to the turn that answers them
 * on the message (`clarifyState`): what was confirmed before these cards, the
 * round, the model the planner pinned, the keys of what has been asked. It got
 * onto the message only from the stream, so a copy fetched from the server had
 * none: opened in another tab, on another device, after the cache was cleared
 * or a stream was cut off. A second card set was then confirmed with round 0,
 * no asked keys and only its own answers; the first set's answers were not
 * sent at all.
 *
 * The runtime keeps the state with the turn (`generationMetrics.clarifyState`).
 * It is put back here, on the message, because that is where every reader
 * already looks: the submit path, the composer, the retry below, the step rows.
 * A message that has its own keeps it.
 */
export function restoreStoredClarifyState<T extends Pick<ChatSession, 'messages'>>(session: T): T {
  if (!session.messages?.some(message => !message.clarifyState && message.generationMetrics?.clarifyState)) {
    return session;
  }
  return {
    ...session,
    messages: session.messages.map(message => (
      !message.clarifyState && message.generationMetrics?.clarifyState
        ? { ...message, clarifyState: message.generationMetrics.clarifyState }
        : message
    )),
  };
}

/**
 * The clarify state a retry has to rebuild, recovered from the messages.
 *
 * The send that answered the cards put `isClarifyFollowUp` and the merged
 * `clarifyState` into `lastConfigRef`, and that is a ref: a reload, a remount
 * or a session switch empties it. The retry then falls back to the session's
 * plain config, which carries neither — and the body of a confirmation turn is
 * only 「问题: 答案」, so the backend, told this is not a follow-up, takes that as
 * the entire requirement.
 *
 * Measured 2026-09-09: a conveyor came back as 「PLC 控制执行器驱动、超时停机、
 * 报警锁存及安全联锁的需求」 — the card topics read as the machine, the conveyor
 * gone — and a fresh round of cards was asked over the top of thirteen just
 * answered. The pinned model rides in the same state, so that turn also
 * switched itself from the model the session had pinned to AutoFree.
 *
 * The turn that asked carries both the state as it stood and the answers given
 * to it, so the merge here is the one the submit path performs.
 */
export function rebuildClarifyFollowUp(
  messages: Message[],
): Pick<GenerationConfig, 'isClarifyFollowUp' | 'clarifyState'> | null {
  const asking = [...messages].reverse().find(
    message => message.role === 'assistant' && message.clarifyAnswers?.length,
  );

  const answered = (asking?.clarifyAnswers || []).map(answer => ({
    id: answer.id,
    category: answer.category,
    question: answer.question,
    selectedOptions: answer.selectedOptions,
    fieldKeys: answer.fieldKeys,
  }));
  if (answered.length === 0) return null;

  const previous = asking?.clarifyState;
  return {
    isClarifyFollowUp: true,
    clarifyState: {
      clarifyRound: previous?.clarifyRound ?? 0,
      pinnedProvider: previous?.pinnedProvider,
      pinnedModel: previous?.pinnedModel,
      confirmedAnswers: [...(previous?.confirmedAnswers ?? []), ...answered],
      askedQuestionKeys: previous?.askedQuestionKeys ?? [],
    },
  };
}
